import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Fix for missing default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

function AddLocation() {
  const [locationName, setLocationName] = useState("");
  const [generatedLocationCode, setGeneratedLocationCode] = useState(null);
  const [locationType, setLocationType] = useState([]);
  const [categories, setCategories] = useState([]);
  const [markerPosition, setMarkerPosition] = useState([6.88639, 79.91899 ]); // India center
  const [latLng, setLatLng] = useState({ lat: null, lng: null });
  const [city, setCity] = useState("");


  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          toast.error("No token found. Please log in.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/locationTypes`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCategories(response.data.locationCategories || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast.error("Error fetching location categories");
      }
    };

    fetchCategories();
  }, []);

  const handleLocationTypeChange = (name) => {
    setLocationType((prev) =>
      prev.includes(name) ? prev.filter((t) => t !== name) : [...prev, name]
    );
  };

  const handleAddLocation = async () => {
    if (!locationName) {
      toast.error("Location name is required.");
      return;
    }

    if (locationType.length === 0) {
      toast.error("Location type is required.");
      return;
    }

    if (!latLng.lat || !latLng.lng) {
      toast.error("Please select a location on the map.");
      return;
    }

    const formData = {
      location_name: locationName,
      location_type: locationType,
      latitude: latLng.lat,
      longitude: latLng.lng,
    };

    try {
      const token = sessionStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.");
        return;
      }

      const response = await axios.post(`${API_BASE_URL}/locations/add`, formData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const { message, location } = response.data;
      toast.success(message || "Location added successfully!");
      setGeneratedLocationCode(location.location_code);
    } catch (error) {
      console.error("Error adding location:", error);
      toast.error("Failed to add location. Please try again.");
    }
  };

  // Custom component to handle map click events
  function LocationMarker() {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        setMarkerPosition([lat, lng]);
        setLatLng({ lat, lng });
        fetchCityFromLatLng(lat, lng); // call reverse geocoding
      },
    });
  
    return <Marker position={markerPosition} />;
  }
  

  const fetchCityFromLatLng = async (lat, lng) => {
    try {
      const response = await axios.get(`https://nominatim.openstreetmap.org/reverse`, {
        params: {
          lat,
          lon: lng,
          format: "json",
        },
      });
  
      const address = response.data.address;
      const cityName = address.city || address.town || address.village || address.hamlet || "Unknown";
      setCity(cityName);
    } catch (error) {
      console.error("Error fetching city name:", error);
      setCity("Unknown");
    }
  };

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Add Location</h2>

        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Location Name</label>
          <input
            type="text"
            className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            placeholder="Enter location name"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
          />
        </div>

        <div className="mb-4 w-full sm:w-1/2">
          <label className="block text-gray-700 mb-2">Location Type</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {categories.map((category) => (
              <label key={category.location_category_code} className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  value={category.location_category_name}
                  checked={locationType.includes(category.location_category_name)}
                  onChange={() => handleLocationTypeChange(category.location_category_name)}
                  className="w-4 h-4 text-green-500 border-gray-300 rounded focus:ring-green-500"
                />
                <span>{category.location_category_name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-gray-700 mb-2">Select Location on Map</label>
          <MapContainer
            center={markerPosition}
            zoom={5}
            scrollWheelZoom={true}
            className="w-full h-96 rounded-lg z-10"
          >
            <TileLayer
              attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <LocationMarker />
          </MapContainer>
          {latLng.lat && latLng.lng && (
            <p className="mt-2 text-sm text-gray-600">
              Selected Coordinates: <strong>Lat:</strong> {latLng.lat.toFixed(5)},{" "}
              <strong>Lng:</strong> {latLng.lng.toFixed(5)} ,
              Detected City: <strong>{city}</strong>
            </p>
          )}
        </div>

        {generatedLocationCode && (
          <div className="mt-4 p-4 bg-gray-100 rounded-lg">
            <p className="text-green-700 font-bold">
              Generated Location Code: {generatedLocationCode}
            </p>
          </div>
        )}

        <div className="mt-6 text-right">
          <button
            onClick={handleAddLocation}
            className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition duration-300"
          >
            Add Location
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddLocation;
